import React, { useState, useEffect, useRef } from 'react'
import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from './../../utilities/BL/Zones'
import { GetDanceConfig } from './../../utilities/BL/Dance'
import { Dance as DanceOverlay } from './Overlays/Dance'

const NORMAL_DANCERS = Array.from({ length: 8 }, (_, i) => `normal/${i + 1}.gif`);
const FAST_DANCERS = Array.from({ length: 14 }, (_, i) => `fast/${i + 1}.gif`);

/**
 * COMPONENTE: DANCE CAM OVERLAY (PARENT ROUTE CONTAINER)
 * ------------------------------------------------------------------
 * 1. Establece conexión WebSocket de SignalR como "overlay:dance".
 * 2. Carga configuración de la API (JSON en disco).
 * 3. Escucha mensajes WebSocket en tiempo real.
 * 4. Controla los atajos de teclado locales (0, 1, 2, 3, Espacio).
 * ------------------------------------------------------------------
 */
const Dance = () => {
    const [config, setConfig] = useState({
        titleText: 'BÁILELE',
        visible: true,
        frameStyle: 'neon-classic',
        eqEffect: 'suave',
        eqGradientStart: '#00ffff',
        eqGradientEnd: '#ff00ff',
        eqGlow: 20,
        floatingShape: 'notes-stars',
        shapeColor: '#00ffff',
        shapeGlow: 15,
        shapeOpacity: 0.3,
        shapeBorderWidth: 2,
        enableDancers: true,
        fogColor: '#250022',
        fogSpeed: 4.0,
        bpm: 120,
        scale: 1.0,
        titleSize: 8.0,
        offsetX: 0,
        offsetY: 0,
        frameZoom: 94
    });

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [activeDancers, setActiveDancers] = useState([]);
    const [fiestaMode, setFiestaMode] = useState(false);
    const [explodeTrigger, setExplodeTrigger] = useState(0);
    const dancerTimeoutRef = useRef(null);

    // Ajustes OBS de pantalla completa
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
            rootElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.removeProperty('background-color');
                rootElement.style.removeProperty('overflow');
            }
        };
    }, []);

    // Conexión y sincronización
    useEffect(() => {
        const initOverlay = async () => {
            const res = await GetDanceConfig();
            if (res.success && res.data) {
                setConfig(prev => ({ ...prev, ...res.data }));
            }

            await ConnectWebSocketForOverlay(
                'dance',
                (status) => setConnectionStatus(status),
                (msg) => console.log('[Dance Overlay] WS status:', msg)
            );

            SetSocketMessageHandler(handleIncomingMessage);
        };

        const handleIncomingMessage = (message) => {
            if (!message || !message.type) return;

            const msgType = message.type.toLowerCase();
            const payload = message.data;

            if (msgType === 'dance') {
                if (payload.action === 'config') {
                    setConfig(prev => ({ ...prev, ...payload.config }));
                } else if (payload.action === 'show') {
                    setConfig(prev => ({ ...prev, visible: true }));
                } else if (payload.action === 'hide') {
                    setConfig(prev => ({ ...prev, visible: false }));
                } else if (payload.action === 'explode') {
                    setExplodeTrigger(prev => prev + 1);
                } else if (payload.action === 'fiesta') {
                    setFiestaMode(payload.state);
                } else if (payload.action === 'dancers') {
                    triggerDancers(payload.isFast);
                }
            }
        };

        initOverlay();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, []);

    // Helper para alternar/disparar bailarines en el frontend
    const triggerDancers = (isFast) => {
        if (dancerTimeoutRef.current) clearTimeout(dancerTimeoutRef.current);
        
        const bank = isFast ? FAST_DANCERS : NORMAL_DANCERS;
        
        // Seleccionar 2 GIFs aleatorios distintos
        const idx1 = Math.floor(Math.random() * bank.length);
        let idx2 = Math.floor(Math.random() * bank.length);
        while (idx1 === idx2 && bank.length > 1) {
            idx2 = Math.floor(Math.random() * bank.length);
        }

        const selectedDancers = [bank[idx1], bank[idx2]];
        setActiveDancers(selectedDancers);

        // Desvanecer después de 8 segundos automáticamente
        dancerTimeoutRef.current = setTimeout(() => {
            const dancersEls = document.querySelectorAll('.dancers-character-gif');
            dancersEls.forEach(el => el.classList.add('dancer-exit'));
            
            setTimeout(() => {
                setActiveDancers([]);
            }, 500);
        }, 8000);
    };

    // Controladores de Teclado Local para OBS Interact
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === '0') {
                setFiestaMode(prev => !prev);
            } else if (e.key === '1') {
                setExplodeTrigger(prev => prev + 1);
            } else if (e.code === 'Space') {
                e.preventDefault();
                setConfig(prev => ({ ...prev, visible: !prev.visible }));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [config.enableDancers]);

    return (
        <>
            <DanceOverlay 
                config={config} 
                activeDancers={activeDancers} 
                fiestaMode={fiestaMode}
                manualExplodeTrigger={explodeTrigger}
            />

            {connectionStatus === 'error' && (
                <div style={{ position: 'fixed', bottom: 0, left: 0, background: '#ef4444', color: 'white', padding: '4px 8px', fontSize: 10, zIndex: 99999 }}>
                    DANCE WS DISCONNECTED
                </div>
            )}
        </>
    );
};

export { Dance };
export default Dance;
